import React, { useEffect, useState, useRef } from 'react'
import AGGrid from "../../../Components/AGGrid";
import { CommonUtil } from "../../../utils/CommonUtil";
import { NetworkManager } from "../../../Components/NetworkManager";
import { Constants } from "../../Languages/Constants";
import Offcanvas from "../../../Components/OffCanvas";
import GenericPopup from "../../../Components/GenericPopup";
import InputField from '../../../Components/InputField';
import TextAreaField from '../../../Components/TextAreaField';
import JSONCompareViewer from '../../../Components/JSONCompareViewer';
import ToastManager from '../../../Components/ToastManager';
import HistoryViewer from './HistoryViewer';

const PendingInvoices = () => {
    const gridRef = useRef<any>(null);
    const popupGridRef = useRef<any>(null);
    const [rowsData, setRowsData] = useState<any>([]);
    const [showPopup, setShowPopup] = useState(false);
    const [selectedRowData, setSelectedRowData] = useState<any>(null);
    const [groupDetails, setGroupDetails] = useState<any>(null);
    const [activeTab, setActiveTab] = useState('invoice');
    const [showHistory, setShowHistory] = useState(false);
    const [historyData, setHistoryData] = useState<any>([]);
    const [itemComments, setItemComments] = useState<any>({});
    const [itemPayloads, setItemPayloads] = useState<any>({});
    const [selectedGroupCodes, setSelectedGroupCodes] = useState<string[]>([]);

    const fetchGroupDetails = (groupCode: string) => {
        NetworkManager.sendJsonRequest(`api/prextra/review/group/${groupCode}`, '', '', 'GET', true, (data: any) => {
            if (data) {
                const parsedData = JSON.parse(data);
                setGroupDetails(parsedData);
                setItemComments({});
                setItemPayloads({});
                setShowPopup(true);
            }
        }, (error: any) => {
            console.error(error);
        });
    }

    const handlePayloadChange = (id: any, newData: any) => {
        setItemPayloads((prev: any) => ({ ...prev, [id]: newData }));
    }

    const handleCommentChange = (id: any, value: any) => {
        setItemComments((prev: any) => ({ ...prev, [id]: value }));
    }

    const handleSave = (sendToPrextra: boolean) => {
        const items = activeTab === 'invoice' ? groupDetails?.invoices :
            activeTab === 'statement' ? groupDetails?.statements :
                groupDetails?.payments;

        if (!items) return;

        items.forEach((item: any) => {
            const itemId = item.id || item.invoiceId || item.documentId;
            const payload = {
                id: itemId,
                payload: itemPayloads[itemId] || item.payload,
                comment: itemComments[activeTab] || "",
                sendToPrextra: sendToPrextra
            };

            NetworkManager.sendJsonRequest('api/prextra/review/save', payload, '', 'POST', true, (res: any) => {
                if (res) {
                    const response = JSON.parse(res);
                    if (!response.errorMessage) {
                        ToastManager.showToast("Saved Successfully", 'Success');
                        fetchGroupDetails(selectedRowData.groupCode);
                    }else{
                        ToastManager.showToast(response.errorMessage, 'Error');
                    }
                }
            }, (err: any) => {
                console.error(err);
            });
        });
    }

    const fetchHistory = () => {
        let id = null;
        
        if (activeTab === 'invoice' && groupDetails?.invoices && groupDetails.invoices.length > 0) {
            id = groupDetails.invoices[0].id;
        } else if (activeTab === 'statement' && groupDetails?.statements && groupDetails.statements.length > 0) {
            id = groupDetails.statements[0].id;
        } else if (activeTab === 'payment' && groupDetails?.payments && groupDetails.payments.length > 0) {
            id = groupDetails.payments[0].id;
        }

        if (!id) {
            ToastManager.showToast("No document found for this tab", 'Warning');
            return;
        }

        NetworkManager.sendJsonRequest(`api/prextra/review/history/pending/${id}`, '', '', 'GET', true, (data: any) => {
            if (data) {
                setHistoryData(JSON.parse(data));
                setShowHistory(true);
            }
        }, (error: any) => {
            console.error(error);
        });
    }

    const invoiceButtonRender = (propData: any) => {
        const onClickInvoice = () => {
            setSelectedRowData(propData.data);
            fetchGroupDetails(propData.data.groupCode);
        }
        return (
            <button className="btn btn-primary btn-sm mb-1" style={{ cursor: 'pointer', minWidth: '200px' }} onClick={onClickInvoice}>{propData.data.groupCode}</button>
        )
    }

    const isRowSelectable = (rowNode: any) => {
        return rowNode.data ? !rowNode.data.isFlagged : true;
    }

    const pendingInvoicesCol: any = [
        {
            headerCheckboxSelection: true,
            headerCheckboxSelectionFilteredOnly: true,
            checkboxSelection: true,
            width: 70,
            maxWidth: 100,
            resizable: false,
            sortable: false,
            filter: false,
            suppressMenu: true,
            cellClass: 'ag-checkbox-center',
            headerClass: 'ag-checkbox-center'
        },
        { field: 'button', headerName: "Invoices", resizable: true, sortable: true, flex: 1, filter: 'agNumberColumnFilter', cellRenderer: invoiceButtonRender },
        { field: 'reservationCode', headerName: CommonUtil.getMessageText(Constants.RESERVATION_CODE, "Reservation Code"), resizable: true, sortable: true, flex: 1, filter: 'agNumberColumnFilter' },
        { field: 'unitNumber', headerName: CommonUtil.getMessageText(Constants.UNIT_NUMBER, "Unit Number"), resizable: true, sortable: true, flex: 1, filter: 'agTextColumnFilter' },
        { field: 'vinNumber', headerName: CommonUtil.getMessageText(Constants.VIN_NUMBER, "VIN Number"), resizable: true, sortable: true, flex: 1, filter: 'agTextColumnFilter' },
        { field: 'invoiceTotalAmount', headerName: "Invoice Total Amount", resizable: true, sortable: true, flex: 1, filter: 'agTextColumnFilter' },
        { field: 'statementTotalAmount', headerName: "Statement Total Amount", resizable: true, sortable: true, flex: 1, filter: 'agTextColumnFilter' },
        { field: 'ownerOrganization', headerName: CommonUtil.getMessageText(Constants.OWNER_ORGANIZATION, "Owner Organization"), resizable: true, sortable: true, flex: 1, filter: 'agNumberColumnFilter' },
        { field: 'renterOrganization', headerName: CommonUtil.getMessageText(Constants.RENTER_ORGANIZATION, "Renter Organization"), resizable: true, sortable: true, flex: 1, filter: 'agNumberColumnFilter' },
    ];

    useEffect(() => {
        fetchPendingBills();
    }, []);

    const fetchPendingBills = () => {
        NetworkManager.sendJsonRequest('api/prextra/review/pendingInvoices', '', '', 'GET', true, (data: any) => {
            if (data) {
                setRowsData(JSON.parse(data));
                setSelectedGroupCodes([]);
            }
        }, (error: any) => {
            console.error(error);
        });
    }

    const handleSelectionChanged = () => {
        const selectedRows = gridRef.current?.getSelectedRows();
        const groupCodes = selectedRows ? selectedRows.map((row: any) => row.groupCode) : [];
        setSelectedGroupCodes(groupCodes);
    }

    const handleSendToPrextraSelected = () => {
        if (selectedGroupCodes.length === 0) return;

        const payload = {
            groupCodes: selectedGroupCodes
        };

        NetworkManager.sendJsonRequest('api/prextra/review/sendToPrextra', payload, '', 'POST', true, (res: any) => {
            if (res) {
                const response = JSON.parse(res);
                if (!response.errorMessage) {
                    ToastManager.showToast("Sent to Prextra Successfully", 'Success');
                    fetchPendingBills();
                } else {
                    ToastManager.showToast(response.errorMessage, 'Error');
                }
            }
        }, (err: any) => {
            console.error(err);
        });
    }

    return (
        <div className="container-fluid mt-3">
            <div className="ag-theme-alpine" style={{ height: "750px", width: "100%" }}>
                <AGGrid
                    id="PendingInvoicesGrid"
                    cols={pendingInvoicesCol}
                    rows={rowsData}
                    pageSize={500}
                    headerHeight={CommonUtil.GRID_HEADER_HEIGHT}
                    rowHeight={CommonUtil.GRID_ROW_HEIGHT}
                    ref={gridRef}
                    addNew={false}
                    rowSelection="multiple"
                    suppressRowClickSelection={true}
                    isRowSelectable={isRowSelectable}
                    onSelectionChanged={handleSelectionChanged}
                    getRowStyle={(params: any) => {
                        if (params.data?.isFlagged) {
                            return {
                                backgroundColor: '#f8d7da',
                                color: '#721c24'
                            };
                        }
                        return {};
                    }}
                />
            </div>
            <div className="d-flex justify-content-end mt-3">
                <button 
                    className="btn btn-primary me-3" 
                    onClick={handleSendToPrextraSelected}
                    disabled={selectedGroupCodes.length === 0}
                >
                    Send To Prextra
                </button>
            </div>

            <Offcanvas
                show={showPopup}
                onHide={() => setShowPopup(false)}
                title={`Invoice Details - ${selectedRowData?.groupCode || ''}`}
                size="90%"
                placement="end"
                scrollable={false}
            >
                <div className="p-3 d-flex flex-column" style={{ height: 'calc(100vh - 70px)' }}>
                    <div className="d-flex justify-content-between align-items-center border-bottom pb-2 mt-2">
                        <ul className="nav nav-tabs border-bottom-0" style={{ marginBottom: "-1px" }}>
                            <li className="nav-item" style={{ cursor: 'pointer' }}>
                                <a className={`nav-link ${activeTab === 'invoice' ? 'active' : ''}`} onClick={() => setActiveTab('invoice')}>
                                    Invoices {groupDetails?.invoices?.[0]?.isFlagged && <i className="fa-solid fa-circle-info text-danger me-1"></i>}
                                </a>
                            </li>
                            <li className="nav-item" style={{ cursor: 'pointer' }}>
                                <a className={`nav-link ${activeTab === 'statement' ? 'active' : ''}`} onClick={() => setActiveTab('statement')}>
                                    Statement {groupDetails?.statements?.[0]?.isFlagged && <i className="fa-solid fa-circle-info text-danger me-1"></i>}
                                </a>
                            </li>
                            <li className="nav-item" style={{ cursor: 'pointer' }}>
                                <a className={`nav-link ${activeTab === 'payment' ? 'active' : ''}`} onClick={() => setActiveTab('payment')}>
                                    Payment
                                </a>
                            </li>
                        </ul>
                        <div className="d-flex align-items-center gap-2">
                            <button className="btn btn-outline-primary btn-sm" onClick={fetchHistory}>View {activeTab} history</button>
                        </div>
                    </div>
                    <div className="tab-content mt-3 flex-grow-1" style={{overflowY: 'hidden', overflowX: 'hidden', paddingRight: '5px' }}>
                        {activeTab === 'invoice' && (
                            <div className="p-2">
                                {groupDetails?.invoices?.map((item: any, idx: number) => (
                                    <div key={idx} className="mb-4 pb-4">
                                        <div className="col-lg-12">
                                            <h6 className="mb-2">Invoice Payload - {item.invoiceCode}</h6>
                                            <JSONCompareViewer 
                                                data={item.payload} 
                                                title={`${item.invoiceCode}`} 
                                                documentId={item.documentId || item.id || item.invoiceId}
                                                documentType="invoice"
                                                height="640px"
                                                onDataChange={(newData) => handlePayloadChange(item.id || item.invoiceId || item.documentId, newData)}
                                                allowAdd={false}
                                                allowDelete={false}
                                                allowCopy={false}
                                                flaggedComments={item.flaggedComments || []}
                                            />
                                        </div>
                                    </div>
                                ))}
                                {(!groupDetails?.invoices || groupDetails.invoices.length === 0) && <div className="p-3">No Invoices Found</div>}
                            </div>
                        )}
                        {activeTab === 'statement' && (
                            <div className="p-2">
                                {groupDetails?.statements?.map((item: any, idx: number) => (
                                    <div key={idx} className="mb-4 pb-4">
                                        <div className="row g-3">
                                            <div className="col-lg-12">
                                                <h6 className="mb-2">Statement Payload - {item.invoiceCode}</h6>
                                                <JSONCompareViewer 
                                                    data={item.payload} 
                                                    title={`${item.invoiceCode}`} 
                                                    documentId={item.documentId || item.id || item.invoiceId}
                                                    documentType="statement"
                                                    height="730px"
                                                    onDataChange={(newData) => handlePayloadChange(item.id || item.invoiceId || item.documentId, newData)}
                                                    allowAdd={false}
                                                    allowDelete={false}
                                                    allowCopy={false}
                                                    flaggedComments={item.flaggedComments || []}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                ))}
                                {(!groupDetails?.statements || groupDetails.statements.length === 0) && <div className="p-3">No Statements Found</div>}
                            </div>
                        )}
                        {activeTab === 'payment' && (
                            <div className="p-2">
                                {groupDetails?.payments?.map((item: any, idx: number) => (
                                    <div key={idx} className="mb-4 pb-4">
                                        <div className="row g-3">
                                            <div className="col-lg-12">
                                                <div className="d-flex justify-content-between align-items-center mb-2">
                                                    <h6 className="mb-0">Payment Payload - {item.invoiceCode}</h6>
                                                    <div className="d-flex align-items-center gap-2">
                                                        <span className="small text-muted">Amount:</span>
                                                        <InputField type="text" className="form-control form-control-sm clsStandardText"
                                                            placeholder="Amount" disabled={true}
                                                            style={{ width: '120px' }}
                                                            value={item.totalAmount} />
                                                    </div>
                                                </div>
                                                <JSONCompareViewer 
                                                    data={item.payload} 
                                                    title={`${item.invoiceCode}`} 
                                                    documentId={item.documentId || item.id || item.invoiceId}
                                                    documentType="payment"
                                                    height="640px"
                                                    onDataChange={(newData) => handlePayloadChange(item.id || item.invoiceId || item.documentId, newData)}
                                                    allowAdd={false}
                                                    allowDelete={false}
                                                    allowCopy={false}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                ))}
                                {(!groupDetails?.payments || groupDetails.payments.length === 0) && <div className="p-3">No Payments Found</div>}
                            </div>
                        )}
                    </div>
                    <div className="d-flex align-items-center gap-2 border-top pt-3 mt-2">
                            <div className="flex-grow-1 mx-2 mt-2" style={{ width: '300px'}}>
                                <TextAreaField
                                    style={{ height: '50px' }}
                                    value={itemComments[activeTab] || ''}
                                    onChange={(e: any) => handleCommentChange(activeTab, e.target.value)}
                                placeholder="Add Comment"
                            />
                        </div>
                        <div className="d-flex gap-2 ms-auto me-2">
                            <button
                                className="btn btn-primary"
                                disabled={Object.values(itemComments).some(
                                    (c: any) => c && c.trim().length > 0 && c.trim().length <= 3
                                )}
                                onClick={() => handleSave(false)}
                            >
                                Save
                            </button>
                            <button
                                className="btn btn-success"
                                disabled={(Object.values(itemComments).some(
                                    (c: any) => c && c.trim().length > 0
                                ) || (groupDetails?.invoices?.[0]?.isFlagged || groupDetails?.statements?.[0]?.isFlagged))}
                                onClick={() => handleSave(true)}
                            >
                                Save & Send to Prextra
                            </button>
                        </div>
                    </div>
                </div>
            </Offcanvas>
            <GenericPopup
                header={`Review ${activeTab} history - ${selectedRowData?.groupCode || ''}`}
                mode={showHistory ? "view" : null}
                onClose={() => setShowHistory(false)}
                fullscreen={true}
            >
                <HistoryViewer historyData={historyData} onClose={() => setShowHistory(false)} />
            </GenericPopup>
        </div>
    )
}

export default PendingInvoices